import React, { useEffect, useState } from 'react';
import reconciliationAPI from './services/reconciliationApi';
import { formatApiError } from '../../utils/errorMessage';
import { formatCalendarDay } from '../../utils/localDate';
import { formatMoney } from '../../utils/money';
import { startingMonthText } from './startMonth';

// Signed by the server's balance rule (transfers follow their direction).
export const signed = (r, currency) => {
  if (r.needs_direction) return `?${formatMoney(Math.abs(Number(r.amount)), currency)}`;
  const e = r.effect !== undefined && r.effect !== null ? Number(r.effect) : (r.transaction_type === 'income' ? 1 : -1) * Number(r.amount);
  return formatMoney(e, currency, { plus: true });
};

// Unreconciled rows dated inside the statement month (start..end inclusive).
// Uses the same calendar-day field the table shows; carried-over rows are left out.
export const monthRowIds = (rows, month, cleared = true) => (rows || [])
  .filter((r) => r.cleared !== cleared && String(r.transaction_date || '').slice(0, 7) === String(month || '').slice(0, 7))
  .map((r) => r.id);

// Outstanding items: rows dated on or before month-end that are not cleared.
// They carry forward to the next month until the bank clears them.
export const outstandingRows = (rows, month) => {
  const end = String(month || '').slice(0, 7);
  return (rows || []).filter((r) => !r.cleared && String(r.transaction_date || '').slice(0, 7) <= end);
};

// Date picker bounds for a ticked row's cleared (bank) date: no earlier than
// the transaction date and inside the month being reconciled.
export const clearedDateBounds = (row, start, end) => {
  const d = String((row && row.transaction_date) || '').slice(0, 10);
  return { min: d > start ? d : start, max: end };
};
const monthEnd = (m) => { const [y, mo] = String(m).slice(0, 7).split('-').map(Number); return `${String(m).slice(0, 7)}-${String(new Date(Date.UTC(y, mo, 0)).getUTCDate()).padStart(2, '0')}`; };

// Rows a Tick all / Untick all would change. Empty means nothing to send.
export const idsToToggle = (rows, cleared) => (rows || []).filter((r) => r.cleared !== cleared).map((r) => r.id);

// A month with no rows can be reconciled when the statement balances agree.
export const zeroActivityNote = (view) => (view && view.period && view.period.status !== 'reconciled' && (view.rows || []).length === 0
  ? (view.math && view.math.balanced
    ? 'No transactions this month. Opening and closing balances match, so you can mark it reconciled.'
    : 'No transactions this month. The closing balance must equal the opening balance to reconcile it.')
  : '');

// Why Mark reconciled is not available: only an exact zero difference can be
// saved as reconciled (the server refuses anything else too).
export const notBalancedText = (math, currency, what = 'this month') => (math && !math.balanced
  ? `The difference is ${formatMoney(math.difference, currency)}. It must be ${formatMoney(0, currency)} before ${what} can be saved as reconciled. Tick the cleared rows or correct the statement balances.`
  : '');

// Plain-language warning when earlier months of this account are still open.
export const earlierOpenWarning = (months) => {
  if (!months || !months.length) return '';
  const list = months.length > 6 ? `${months.slice(0, 6).join(', ')} and ${months.length - 6} more` : months.join(', ');
  return `Earlier months are not reconciled yet (${list}). You can still mark this month reconciled, but those months stay open.`;
};

// One account and month: statement balances, rows to tick, live difference,
// and Reconcile / Reopen.
export default function PeriodPanel({ account, month, onChanged }) {
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ opening_balance: '', closing_balance: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const accept = (data) => {
    setView(data);
    const p = data.period;
    setForm({
      opening_balance: p ? p.opening_balance : (data.suggested_opening ?? ''),
      closing_balance: p ? p.closing_balance : ''
    });
  };

  const run = async (fn, after) => {
    setBusy(true);
    setError('');
    try {
      const res = await fn();
      if (res && res.data && res.data.rows) accept(res.data);
      if (after) after(res);
      onChanged();
    } catch (err) {
      setError(formatApiError(err, 'That did not work'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let live = true;
    setView(null);
    reconciliationAPI.getMonth(account.account_id, month)
      .then((res) => { if (live) accept(res.data); })
      .catch((err) => { if (live) setError(formatApiError(err, 'Could not load this month')); });
    return () => { live = false; };
  }, [account.account_id, month]);

  if (!view) return <div className="p-4 text-gray-500">{error || 'Loading…'}</div>;
  const locked = view.period && view.period.status === 'reconciled';
  const id = account.account_id;
  const cur = account.currency_code;
  const save = (e) => { e.preventDefault(); run(() => reconciliationAPI.saveMonth(id, month, form)); };
  const setDate = (row, value) => value && run(() => reconciliationAPI.clearRows(id, month, [row.id], true, { [row.id]: value }));
  const picks = view.dating_method === 'transaction_date' && !locked;
  const toggle = (row) => run(() => reconciliationAPI.clearRows(id, month, [row.id], !row.cleared));
  const tickAll = (cleared) => { const ids = idsToToggle(view.rows, cleared); if (ids.length) run(() => reconciliationAPI.clearRows(id, month, ids, cleared)); };
  const tickMonth = () => { const ids = monthRowIds(view.rows, month); if (ids.length) run(() => reconciliationAPI.clearRows(id, month, ids, true)); };
  const earlierWarning = earlierOpenWarning(view.earlier_open_months);
  const upload = (file) => file && run(() => reconciliationAPI.readStatement(id, month, file), (res) => {
    const d = res.data || {};
    setForm((f) => ({ opening_balance: d.opening_balance ?? f.opening_balance, closing_balance: d.closing_balance ?? f.closing_balance }));
    if (d.opening_balance === null && d.closing_balance === null) setError('No balances found in that file. Type them in.');
  });

  return (
    <div className="bg-white rounded shadow p-4 space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold">{account.account_name} · {month.slice(0, 7)}</h3>
          {startingMonthText(account, month) && <p className="text-sm text-blue-700" data-testid="starting-month">{startingMonthText(account, month)}</p>}
          {account.is_liability && <p className="text-xs text-gray-500">Enter statement balances as the amount owed (positive).</p>}
          {locked && <p className="text-sm text-green-700">Reconciled {String(view.period.reconciled_at || '').slice(0, 10)}. Cleared transactions are locked.</p>}
        </div>
        {view.math && <div className={`text-right ${view.math.balanced ? 'text-green-700' : 'text-red-700'}`}>
          <div className="text-xs">Difference</div><div className="text-2xl font-bold">{formatMoney(view.math.difference, cur)}</div>
          <div className="text-xs text-gray-500">Cleared balance {formatMoney(view.math.cleared_balance, cur)} ({view.math.cleared_count} rows)</div>
          {view.dating_method === 'transaction_date' && view.math.book_balance !== undefined && <div className="text-xs text-gray-500" data-testid="book-vs-statement">
            Book balance {formatMoney(view.math.book_balance, cur)} minus outstanding {formatMoney(view.math.outstanding_net, cur)} ({view.math.outstanding_count} items)
          </div>}
        </div>}
      </div>
      {error && <p className="text-red-600 text-sm" role="alert">{error}</p>}
      <form onSubmit={save} className="flex flex-wrap items-end gap-3 text-sm">
        {['opening_balance', 'closing_balance'].map((k) => (
          <label key={k}>{k === 'opening_balance' ? 'Statement opening balance' : 'Statement closing balance'}
            <input type="number" step="0.01" required disabled={locked} value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="block border rounded px-2 py-1" />
          </label>
        ))}
        <button type="submit" disabled={busy || locked} className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50">Save balances</button>
        <label className="text-xs text-gray-600">or read from a CSV/TXT statement
          <input type="file" accept=".csv,.txt,text/csv,text/plain" disabled={busy || locked} onChange={(e) => upload(e.target.files[0])} className="block" />
        </label>
      </form>
      {view.period && (
        <div>
          <div className="flex gap-2 mb-2 text-sm">
            <button type="button" disabled={busy || locked || !idsToToggle(view.rows, true).length} onClick={() => tickAll(true)} className="px-2 py-1 border rounded disabled:opacity-50">Tick all</button>
            <button type="button" disabled={busy || locked || !monthRowIds(view.rows, month).length} onClick={tickMonth} className="px-2 py-1 border rounded disabled:opacity-50" title="Tick only rows dated within this statement month">Tick this month only</button>
            <button type="button" disabled={busy || locked || !idsToToggle(view.rows, false).length} onClick={() => tickAll(false)} className="px-2 py-1 border rounded disabled:opacity-50">Untick all</button>
            <span className="flex-1" />
            {locked
              ? <button type="button" disabled={busy} onClick={() => run(() => reconciliationAPI.reopenMonth(id, month))} className="px-3 py-1 border border-orange-500 text-orange-700 rounded">Reopen month</button>
              : <button type="button" disabled={busy || !(view.math && view.math.balanced)} onClick={() => run(() => reconciliationAPI.reconcileMonth(id, month))} className="px-3 py-1 bg-green-600 text-white rounded disabled:opacity-50">Mark reconciled</button>}
          </div>
          {!locked && notBalancedText(view.math, cur) && <p className="text-sm text-red-700 mb-2" role="status" data-testid="not-balanced">{notBalancedText(view.math, cur)}</p>}
          {zeroActivityNote(view) && <p className="text-sm text-gray-600 mb-2" role="status">{zeroActivityNote(view)}</p>}
          {!locked && earlierWarning && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-300 rounded px-2 py-1 mb-2" role="status">{earlierWarning}</p>}
          <div className="max-h-96 overflow-y-auto border rounded">
            <table className="min-w-full text-sm">
              <tbody>
                {view.rows.map((r) => (
                  <tr key={r.id} className={`border-t ${r.cleared ? 'bg-green-50' : ''}`}>
                    <td className="px-2 py-1"><input type="checkbox" checked={r.cleared} disabled={busy || locked} onChange={() => toggle(r)} aria-label="Cleared" /></td>
                    <td className="px-2 py-1 whitespace-nowrap">{formatCalendarDay(r.transaction_date)}</td>
                    <td className="px-2 py-1">{r.description}{r.transaction_date.slice(0, 7) < month.slice(0, 7) ? ' (carried over)' : ''}{!r.cleared && view.dating_method === 'transaction_date' ? <span className="ml-1 text-xs text-amber-700">Outstanding</span> : null}{picks && r.cleared ? <input type="date" aria-label="Cleared date" className="ml-2 border rounded px-1 text-xs" disabled={busy} value={r.tick_cleared_date || ''} min={clearedDateBounds(r, month.slice(0, 10), monthEnd(month)).min} max={monthEnd(month)} onChange={(e) => setDate(r, e.target.value)} /> : null}{r.cleared_date ? <span className="ml-1 text-xs text-gray-500">Cleared {formatCalendarDay(r.cleared_date)}</span> : null}</td>
                    <td className="px-2 py-1 text-right font-mono">{signed(r, cur)}</td>
                  </tr>
                ))}
                {!view.rows.length && <tr><td className="p-3 text-gray-500">No transactions for this month.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
